import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";

const SIZE = 4;
const empty = () => Array(SIZE * SIZE).fill(0);

function addTile(g) {
  const cells = g.map((v, i) => (v === 0 ? i : -1)).filter((i) => i >= 0);
  if (!cells.length) return g;
  const i = cells[Math.floor(Math.random() * cells.length)];
  const ng = [...g];
  ng[i] = Math.random() < 0.9 ? 2 : 4;
  return ng;
}

function slide(row) {
  let arr = row.filter((v) => v);
  let gained = 0;
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] === arr[i + 1]) { arr[i] *= 2; gained += arr[i]; arr[i + 1] = 0; }
  }
  arr = arr.filter((v) => v);
  while (arr.length < SIZE) arr.push(0);
  return { row: arr, gained };
}

const to2D = (g) => Array.from({ length: SIZE }, (_, r) => g.slice(r * SIZE, r * SIZE + SIZE));
const flat = (m) => m.flat();
const transpose = (m) => m[0].map((_, c) => m.map((row) => row[c]));

function move(g, dir) {
  let m = to2D(g);
  let gained = 0;
  const rev = (row) => [...row].reverse();
  if (dir === "up") m = transpose(m);
  if (dir === "down") m = transpose(m).map(rev);
  if (dir === "right") m = m.map(rev);
  m = m.map((row) => { const { row: r, gained: gg } = slide(row); gained += gg; return r; });
  if (dir === "right") m = m.map(rev);
  if (dir === "down") m = transpose(m.map(rev));
  if (dir === "up") m = transpose(m);
  return { grid: flat(m), gained };
}

function canMove(g) {
  if (g.includes(0)) return true;
  const m = to2D(g);
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      if (c < SIZE - 1 && m[r][c] === m[r][c + 1]) return true;
      if (r < SIZE - 1 && m[r][c] === m[r + 1][c]) return true;
    }
  return false;
}

const COLORS = {
  0: "#efece5", 2: "#e6e2d8", 4: "#ddd8ca", 8: "#cfc8b5", 16: "#bcb39c",
  32: "#a69c82", 64: "#8c8069", 128: "#6f6552", 256: "#524a3b", 512: "#3a342a",
  1024: "#262219", 2048: "#141414",
};
const textLight = (v) => v >= 128;

export function Game2048() {
  const [grid, setGrid] = useState(() => addTile(addTile(empty())));
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("rea_2048_best") || 0));
  const [over, setOver] = useState(false);
  const [won, setWon] = useState(false);
  const ref = useRef({ grid, over, won });
  ref.current = { grid, over, won };

  const handleMove = useCallback((dir) => {
    const { grid: g, over: o } = ref.current;
    if (o) return;
    const { grid: ng, gained } = move(g, dir);
    if (JSON.stringify(ng) === JSON.stringify(g)) return;
    const withTile = addTile(ng);
    setGrid(withTile);
    if (gained) setScore((s) => {
      const ns = s + gained;
      if (ns > best) { setBest(ns); localStorage.setItem("rea_2048_best", String(ns)); }
      return ns;
    });
    if (!won && ng.includes(2048)) setWon(true);
    if (!canMove(withTile)) setOver(true);
  }, [best, won]);

  useEffect(() => {
    const onKey = (e) => {
      const map = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" };
      if (map[e.key]) { e.preventDefault(); handleMove(map[e.key]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleMove]);

  const touch = useRef(null);
  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x, dy = t.clientY - touch.current.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
    handleMove(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : (dy > 0 ? "down" : "up"));
    touch.current = null;
  };

  const reset = () => { setGrid(addTile(addTile(empty()))); setScore(0); setOver(false); setWon(false); };

  return (
    <div className="border border-border bg-card p-6 md:p-10" data-testid="game-2048">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h4 className="font-display text-2xl font-extrabold uppercase text-foreground">2048 · Build Wealth</h4>
          <p className="font-mono text-xs text-muted-foreground">Merge tiles with arrow keys or swipe. Reach 2048!</p>
        </div>
        <div className="flex gap-3">
          <div className="border border-border bg-background px-4 py-2 text-center">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Score</p>
            <p className="font-mono text-xl font-bold text-foreground" data-testid="g2048-score">{score}</p>
          </div>
          <div className="border border-border bg-background px-4 py-2 text-center">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Best</p>
            <p className="font-mono text-xl font-bold text-foreground">{best}</p>
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-md">
        <div className="grid grid-cols-4 gap-2 border border-border bg-secondary p-2 touch-none select-none"
             onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="g2048-board">
          {grid.map((v, i) => (
            <div key={i} className="flex aspect-square items-center justify-center font-mono font-bold transition-colors"
                 style={{ background: COLORS[v] || "#141414", color: textLight(v) ? "#fff" : "#141414", fontSize: v >= 1024 ? "1.1rem" : v >= 100 ? "1.4rem" : "1.8rem" }}>
              {v > 0 ? v : ""}
            </div>
          ))}
        </div>
        {(over || won) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background/90 backdrop-blur" data-testid="g2048-overlay">
            <p className="font-display text-4xl font-black uppercase text-foreground">{won && !over ? "You hit 2048!" : "Game Over"}</p>
            <p className="font-mono text-sm text-muted-foreground">Score {score}</p>
            <Button onClick={reset} className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90" data-testid="g2048-restart"><RotateCcw className="mr-2 h-4 w-4" /> Play Again</Button>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col items-center gap-2 md:hidden">
        <button onClick={() => handleMove("up")} className="border border-border p-3" aria-label="up"><ArrowUp className="h-5 w-5" /></button>
        <div className="flex gap-2">
          <button onClick={() => handleMove("left")} className="border border-border p-3" aria-label="left"><ArrowLeft className="h-5 w-5" /></button>
          <button onClick={() => handleMove("down")} className="border border-border p-3" aria-label="down"><ArrowDown className="h-5 w-5" /></button>
          <button onClick={() => handleMove("right")} className="border border-border p-3" aria-label="right"><ArrowRight className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <Button variant="outline" onClick={reset} className="rounded-none border-border font-mono text-xs uppercase tracking-widest hover:border-foreground" data-testid="g2048-newgame"><RotateCcw className="mr-2 h-4 w-4" /> New Game</Button>
      </div>
    </div>
  );
}
